import React, { useContext, useCallback } from 'react';
import ReactDOM from 'react-dom';
import propTypes from 'prop-types';
import ConfirmDialog from 'chameleon/packages/blue-book-player/library/modal/ConfirmDialog';
import Icon from 'chameleon/packages/blue-book-player/library/icon';
import dateTimeFormat from 'chameleon/packages/blue-book-player/utility/DateTime';
import { MODAL_INNER_ROOT } from 'chameleon/packages/blue-book-player/constants/selectors';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import { isImpersonating } from 'chameleon/packages/blue-book-player/utils';
import { BluebookContext } from 'chameleon/packages/blue-book-player/state/BluebookContext';
import { learnosityExitPlayer } from 'chameleon/packages/blue-book-player/utility/learnosityUtils/submit';

/**
 * Renders the modal for exiting an assignment while taking it.
 * This is a confirmation modal for student.
 * */
const ExitAssignmentModal = ({
  allowLateSubmissionFF,
  allowHardTimerFF,
  save,
  onClose,
  params: { subject },
  router,
}) => {
  const { state: playerState } = useContext(BluebookContext);
  const playerEl = useGetPlayerEl();
  const timer = playerState?.assignmentPlayer?.timer;
  const { isHardTimer, allowLateSubmission, timezone, dueAt } =
    playerState?.assignment ?? {};
  const locationState = router?.location?.state;

  const onExitPlayer = () => {
    learnosityExitPlayer(subject, router, locationState);
  };

  const learnositySave = useCallback(() => {
    // in impersonation mode, don't save to LRN
    if (isImpersonating()) {
      onExitPlayer();
      return true;
    }

    const saveSetting = {
      success: () => {
        onExitPlayer();
      },
      error: () => {},
    };
    save(saveSetting);
  });

  const dueAtDate = dateTimeFormat(dueAt, 'dateMonthTextAndDay', timezone);
  const dueAtHour = dateTimeFormat(dueAt, 'timeHourSeconds', timezone);

  const lateSubmissionText = allowLateSubmissionFF
    ? `page in your Assignments, unless your teacher has chosen to allow for late submissions`
    : `view of “Assignments” at that time`;

  const ConfirmDialogContent = () => {
    const isHardDueAt = !allowLateSubmission;

    const hasDueAtAndTimer = dueAt && timer;
    const hasNoDueAtAndHasTimer = !dueAt && timer;
    const hasNoTimerAndHasDueAt = !timer && dueAt;

    if (hasDueAtAndTimer && isHardDueAt && isHardTimer) {
      return (
        <>
          <p className="m-0">
            This assignment has a strict time limit and is due on{' '}
            <b>
              {dueAtDate} at {dueAtHour}
            </b>
            . Your progress will be automatically submitted once the timer
            expires or the due date is reached, whichever comes first.
          </p>
          <p className="m-0">Do you want to exit the assignment?</p>
        </>
      );
    }

    if (hasDueAtAndTimer && isHardDueAt && !isHardTimer) {
      return (
        <>
          <p className="m-0">
            This is a timed assignment and is due on{' '}
            <b>
              {dueAtDate} at {dueAtHour}
            </b>
            . Your teacher has disabled the strict time limit for this
            assignment and you can continue to work on this after the timer
            expires until the due date is reached. At that time your work will
            be automatically submitted. There is no way to pause the timer.
          </p>
          <p className="m-0">Do you want to exit the assignment?</p>
        </>
      );
    }

    if (hasDueAtAndTimer && !isHardDueAt && isHardTimer) {
      return (
        <>
          <p className="m-0">
            This is a timed assessment with a strict time limit. If you leave
            now, the timer will continue to run and your responses will be
            submitted once the timer reaches 00:00. There is no way to pause the
            timer.
          </p>
          <p className="m-0">Are you sure you want to leave this page?</p>
        </>
      );
    }

    if (hasDueAtAndTimer && !isHardDueAt && !isHardTimer) {
      return (
        <>
          <p className="m-0">
            This is a timed assessment. If you leave now, the timer will
            continue to run. Your teacher has disabled the strict time limit for
            this assignment and it will NOT automatically submit when the timer
            expires. There is no way to pause the timer.
          </p>
          <p className="m-0">Are you sure you want to leave this page?</p>
        </>
      );
    }

    if (hasNoTimerAndHasDueAt && isHardDueAt) {
      return (
        <>
          <p className="m-0">
            This assignment is due on{' '}
            <b>
              {dueAtDate} at {dueAtHour}
            </b>
            . If you don’t submit it before then, it will be automatically
            submitted and moved to your “Completed” {lateSubmissionText}.
          </p>
          <p className="m-0">Do you want to exit the assignment?</p>
        </>
      );
    }

    if (hasNoTimerAndHasDueAt && !isHardDueAt) {
      return (
        <>
          <p className="m-0">
            This assignment is due on{' '}
            <b>
              {dueAtDate} at {dueAtHour}
            </b>
            . If you don’t submit it before then, it will be automatically
            submitted and moved to your “Completed” {lateSubmissionText}.
          </p>
          <p className="m-0">Do you want to exit the assignment?</p>
        </>
      );
    }

    if (hasNoDueAtAndHasTimer && isHardTimer) {
      return (
        <>
          <p className="m-0">
            This is a timed assessment with a strict time limit. If you leave
            now, the timer will continue to run and your responses will be
            submitted once the timer reaches 00:00. There is no way to pause the
            timer.
          </p>
          <p className="m-0">Are you sure you want to leave this page?</p>
        </>
      );
    }

    if (hasNoDueAtAndHasTimer && !isHardTimer) {
      return (
        <>
          <p className="m-0">
            This is a timed assessment. If you leave now, the timer will
            continue to run. Your teacher has disabled the strict time limit for
            this assignment and it will NOT automatically submit when the timer
            expires. There is no way to pause the timer.
          </p>
          <p className="m-0">Are you sure you want to leave this page?</p>
        </>
      );
    }

    if (!dueAt && !timer) {
      return <p className="m-0">Do you want to exit the assignment?</p>;
    }
  };

  return ReactDOM.createPortal(
    <ConfirmDialog
      size="big"
      title="Save and Exit"
      onClose={onClose}
      onSuccess={learnositySave}
      aria={{
        'aria-live': 'polite',
      }}
      content={{
        btnPrimary: 'Yes',
        btnSecondary: 'No',
      }}
    >
      {allowHardTimerFF ? (
        <div className="flex flex-col gap-8">
          <p className="m-0">
            Your progress has been saved, and you have not yet submitted the
            assignment.
          </p>
          <ConfirmDialogContent />
        </div>
      ) : dueAt ? (
        timer ? (
          <div className="flex flex-col gap-8">
            <div className="flex gap-4 items-center">
              <p className="m-0">
                This is a timed assignment that is due on {dueAtDate} at{' '}
                {dueAtHour}.
              </p>
            </div>
            <p className="m-0">
              If you exit now, your progress will be saved and the timer will
              continue to run. If you don’t submit this assignment before the
              due date, it will be automatically submitted at that time and
              moved to your “Completed” {lateSubmissionText}.
            </p>
            <p className="m-0">Do you want to exit the assignment?</p>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <p className="m-0">
              Your progress has been saved, and you have not yet submitted the
              assignment.
            </p>
            <p className="m-0">
              This assignment is due on {dueAtDate} at {dueAtHour}. If you don’t
              submit it before then, it will be automatically submitted and
              moved to your “Completed” {lateSubmissionText}.
            </p>
            <p className="m-0">Do you want to exit the assignment?</p>
          </div>
        )
      ) : timer ? (
        <div className="flex flex-col gap-8">
          <div className="flex gap-4 items-center">
            <Icon id="apc_timer" />
            <p className="m-0">This is a timed assessment.</p>
          </div>
          <p className="m-0">
            If you leave now, the timer will continue to run and your response
            will not be submitted.
          </p>
          <p className="m-0">Are you sure you want to leave this page?</p>
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <p className="m-0">Your progress has been saved.</p>
          <p className="m-0">You have not yet submitted the assignment.</p>
          <p className="m-0">Do you want to exit the assignment?</p>
        </div>
      )}
    </ConfirmDialog>,
    playerEl?.querySelector(MODAL_INNER_ROOT),
  );
};

ExitAssignmentModal.propTypes = {
  save: propTypes.func,
  onClose: propTypes.func,
  allowLateSubmissionFF: propTypes.bool,
};

ExitAssignmentModal.defaultProps = {
  save: () => {},
  onClose: () => {},
  allowLateSubmissionFF: false,
};

export default ExitAssignmentModal;
